import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export type CandidateStatusValue = 'pending' | 'approved' | 'rejected';

export interface CandidateLocation {
  address?: string;
  city?: string;
  country?: string;
  longitude?: number;
  latitude?: number;
}

export interface EventCandidate {
  id: string;
  source: string;
  externalId: string;
  status: CandidateStatusValue;
  name: string;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  timezone: string | null;
  location: CandidateLocation | null;
  imageUrl: string | null;
  ticketUrl: string | null;
  fee: number | null;
  currency: string | null;
  category: string | null;
  sourceUrl: string | null;
  // Raw extracted payload (schema.org JSON-LD node, goout feeder item, or —
  // for LLM-backed sources like telegram/kompas — the original post snapshot
  // plus the model's extraction). Rendered on the detail page for review.
  rawPayload?: unknown;
  approvedEventId: string | null;
  possibleDuplicateOf: string | null;
  firstSeenAt: string;
  lastSeenAt: string | null;
  updatedAt: string;
}

export interface ListCandidatesResponse {
  items: EventCandidate[];
  total: number;
  page: number;
  limit: number;
}

export interface ListCandidatesArgs {
  status?: CandidateStatusValue;
  city?: string;
  source?: string;
  page?: number;
  limit?: number;
}

export interface CandidateCity {
  city: string;
  count: number;
}

export interface CandidateSource {
  source: string;
  count: number;
}

export interface CandidateCitiesArgs {
  status?: CandidateStatusValue;
}

// approve() materializes a live Event; we only need enough to link to it.
export interface ApprovedEvent {
  id: string;
  name: string;
}

export interface SourceRunResult {
  source: string;
  discovered?: number;
  inserted?: number;
  updated?: number;
  skipped?: number;
  error?: string;
}

export interface IngestionReport {
  startedAt: string;
  finishedAt: string;
  sources: SourceRunResult[];
}

// run-ingestion now fires the run in the background and returns immediately
// (the full run is longer than the nginx proxy timeout). The report is no
// longer returned synchronously — only whether the run was kicked off.
export interface RunIngestionResponse {
  status: 'started' | 'already-running';
}

// Live progress of the current/most-recent run, polled while a run is active.
// null when no run has happened since the server started.
export interface IngestionProgress {
  running: boolean;
  startedAt: string;
  finishedAt: string | null;
  totalSources: number;
  completedSources: number;
  currentSource: string | null;
  staleRemoved: number;
  sources: SourceRunResult[];
}

export const candidatesApi = createApi({
  reducerPath: 'candidatesApi',
  baseQuery,
  tagTypes: ['Candidate', 'CandidateList'],
  endpoints: (builder) => ({
    listCandidates: builder.query<ListCandidatesResponse, ListCandidatesArgs>({
      query: ({ status, city, source, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (city) params.set('city', city);
        if (source) params.set('source', source);
        params.set('page', String(page));
        params.set('limit', String(limit));
        return { url: `/admin/event-candidates?${params.toString()}` };
      },
      providesTags: ['CandidateList'],
    }),
    listCandidateCities: builder.query<CandidateCity[], CandidateCitiesArgs>({
      query: ({ status }) => {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        return { url: `/admin/event-candidates/cities?${params.toString()}` };
      },
      providesTags: ['CandidateList'],
    }),
    listCandidateSources: builder.query<
      CandidateSource[],
      CandidateCitiesArgs
    >({
      query: ({ status }) => {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        return { url: `/admin/event-candidates/sources?${params.toString()}` };
      },
      providesTags: ['CandidateList'],
    }),
    getCandidate: builder.query<EventCandidate, string>({
      query: (id) => ({ url: `/admin/event-candidates/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Candidate', id }],
    }),
    approveCandidate: builder.mutation<ApprovedEvent, string>({
      query: (id) => ({
        url: `/admin/event-candidates/${id}/approve`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Candidate', id },
        'CandidateList',
      ],
    }),
    rejectCandidate: builder.mutation<EventCandidate, string>({
      query: (id) => ({
        url: `/admin/event-candidates/${id}/reject`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Candidate', id },
        'CandidateList',
      ],
    }),
    runIngestion: builder.mutation<RunIngestionResponse, void>({
      query: () => ({
        url: `/admin/event-candidates/run-ingestion`,
        method: 'POST',
      }),
      invalidatesTags: ['CandidateList'],
    }),
    ingestionStatus: builder.query<IngestionProgress | null, void>({
      query: () => ({ url: `/admin/event-candidates/ingestion-status` }),
    }),
  }),
});

export const {
  useListCandidatesQuery,
  useListCandidateCitiesQuery,
  useListCandidateSourcesQuery,
  useGetCandidateQuery,
  useApproveCandidateMutation,
  useRejectCandidateMutation,
  useRunIngestionMutation,
  useIngestionStatusQuery,
} = candidatesApi;
